import { Box, BoxProps } from "@chakra-ui/react"
import { useEffect, useRef } from "react"

type Props = {
  id: string
  autoplay: boolean
  backgroundMode: boolean
  muted: boolean
  containerStyles?: BoxProps
}

const TrailerLoom = ({
  id,
  autoplay,
  backgroundMode,
  muted,
  containerStyles
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)

  // Mount iframe once per id; control via postMessage after mount
  useEffect(() => {
    if (!ref.current) return
    const container = ref.current
    const params = new URLSearchParams({
      autoplay: autoplay ? "1" : "0",
      // Start muted to satisfy autoplay; will unmute in focused mode via postMessage
      muted: "1",
      hide_share: "1",
      hide_title: "1",
      loop: backgroundMode ? "1" : "0"
    })
    container.innerHTML = `<iframe width=\"100%\" height=\"100%\" src=\"https://www.loom.com/embed/${id}?${params.toString()}\" frameborder=\"0\" allow=\"autoplay; fullscreen; clipboard-write\" allowfullscreen></iframe>`
  }, [id])

  useEffect(() => {
    const iframe = ref.current?.querySelector(
      "iframe"
    ) as HTMLIFrameElement | null
    if (!iframe) return
    const loomTarget = "https://www.loom.com"
    const send = (msg: any) => {
      try {
        iframe.contentWindow?.postMessage(JSON.stringify(msg), loomTarget)
      } catch (_) {}
    }
    // Clear any prior retries
    const timeouts: number[] = []
    const schedule = (fn: () => void, ms: number) => {
      const id = window.setTimeout(fn, ms)
      timeouts.push(id)
    }

    if (!backgroundMode && !muted) {
      // Focused and unmuted: request play, then repeatedly try to unmute/raise volume shortly after
      send({ type: "play" })
      const attempts = [30, 80, 160, 320, 640, 1200]
      attempts.forEach((ms) => {
        schedule(() => {
          send({ type: "setMuted", payload: { muted: false } })
          send({ type: "setVolume", payload: { volume: 1 } })
        }, ms)
      })
    } else {
      // Background or muting
      const attempts = [0, 100, 300]
      attempts.forEach((ms) => {
        schedule(() => {
          send({ type: "setMuted", payload: { muted: !!muted } })
          send({ type: "setVolume", payload: { volume: muted ? 0 : 1 } })
        }, ms)
      })
    }

    return () => {
      timeouts.forEach((id) => window.clearTimeout(id))
    }
  }, [muted, backgroundMode])

  return (
    <Box
      ref={ref}
      position="relative"
      css={{
        aspectRatio: "16/9",
        "& iframe": { width: "100%", height: "100%", border: "none" }
      }}
      {...containerStyles}
    />
  )
}

export default TrailerLoom
